import { useState } from "react";

interface Props {
  productName: string;
  batch: string;
  bagNumber?: string;
  onConfirm: (count: number) => void;
  onCancel: () => void;
}

/**
 * Prompt for the number of bags after scanning a NON-serial (lot-tracked) product — the bags
 * of a batch are identical, so the operator scans one and enters the count instead of scanning
 * the same barcode repeatedly. Serial products don't use this (each bag is a unique scan).
 */
export function BagCountPrompt({ productName, batch, bagNumber, onConfirm, onCancel }: Props) {
  // Start blank so the operator types the count fresh (no leftover "1" to edit around).
  const [value, setValue] = useState("");
  const count = Math.floor(Number(value));
  const valid = value.trim() !== "" && Number.isFinite(count) && count >= 1 && count <= 9999;

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/70 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-5">
        <h3 className="text-lg font-bold">How many bags?</h3>
        <p className="mt-1 text-sm leading-tight text-gray-300">{productName}</p>
        <p className="mb-4 font-mono text-xs text-gray-500">
          Batch {batch}
          {bagNumber ? ` · Bag ${bagNumber}` : ""}
        </p>

        <div className="mb-5 flex items-center justify-center gap-4">
          <button
            onClick={() => setValue(String(Math.max(1, (valid ? count : 1) - 1)))}
            className="h-14 w-14 shrink-0 rounded-xl bg-gray-700 text-3xl font-bold active:bg-gray-600"
          >
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            placeholder="0"
            value={value}
            // Digits only — strip anything else (no ".", "-", "e", spaces) and cap at 4 digits.
            onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
            className="w-24 rounded-xl border border-gray-700 bg-gray-900 py-3 text-center text-3xl font-bold text-white placeholder:text-gray-600 focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={() => setValue(String((valid ? count : 0) + 1))}
            className="h-14 w-14 shrink-0 rounded-xl bg-gray-700 text-3xl font-bold active:bg-gray-600"
          >
            +
          </button>
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600">
            Cancel
          </button>
          <button
            onClick={() => valid && onConfirm(count)}
            disabled={!valid}
            className="flex-1 rounded-xl bg-blue-600 py-3 font-semibold active:bg-blue-700 disabled:opacity-40"
          >
            {valid ? `Add ${count} bag${count !== 1 ? "s" : ""}` : "Add bags"}
          </button>
        </div>
      </div>
    </div>
  );
}
